import { useCallback, useEffect, useRef, useState } from 'react';
import { downloadWorkbook } from '../../../AnalyticsHub/utils/workbookDownload';
import type { WesFilterSelection } from './useWesPalletStagingFilters';
import { fetchWesExport } from './wesPalletStagingApi';
import { buildWesWorkbook, exportFileName } from './wesPalletStagingExport';
import { buildWesReadRequest, dimensionSortOf, sortRowsByDimension } from './wesPalletStagingRequest';
import type { WesDimKey, WesDisplayMode, WesTableSort } from './wesPalletStagingTable';

export type WesExportState = 'idle' | 'working' | 'done' | 'error';

interface UseWesLocationExportOptions {
  applied: WesFilterSelection;
  periodWeeks: any[];
  dimensions: WesDimKey[];
  sort: WesTableSort | null;
  mode: WesDisplayMode;
}

export const WES_EXPORT_SUCCESS = 'Export Successful.';
export const WES_EXPORT_FAILURE = 'Export Failed.';

// Same filters, dimensions, sort and mode as the table, with no page: the API returns every grouped row.
export function useWesLocationExport({ applied, periodWeeks, dimensions, sort, mode }: UseWesLocationExportOptions) {
  const [state, setState] = useState<WesExportState>('idle');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const run = useCallback(async () => {
    if (state === 'working') return;
    if (timer.current) clearTimeout(timer.current);
    const built = buildWesReadRequest(applied, periodWeeks, { dimensions, sort, displayMode: mode });
    if (!built.ok) {
      setState('error');
      return;
    }
    setState('working');
    try {
      const data = await fetchWesExport(built.request);
      const dimensionSort = dimensionSortOf(sort, dimensions);
      const sorted = dimensionSort ? { ...data, rows: sortRowsByDimension(data.rows, dimensionSort.key, dimensionSort.dir) } : data;
      await downloadWorkbook(buildWesWorkbook(sorted, dimensions, mode), exportFileName());
      setState('done');
      timer.current = setTimeout(() => setState('idle'), 4000);
    } catch {
      setState('error');
    }
  }, [state, applied, periodWeeks, dimensions, sort, mode]);

  return { state, run };
}
